import { User as FirebaseUser } from 'firebase/auth';
import { collection, doc, setDoc, updateDoc } from 'firebase/firestore';
import { nanoid } from 'nanoid';
import { db } from './firebase';
import { runTransaction } from './firestore';
import { getUserAccountByEmail } from './users';
import { buildNewPrivateInfoDoc, buildNewUserAccountDoc, normalizeEmail, profileFromUser } from './newUserAccountDoc';
import { createAccountReviewIfAbsent } from './accountReviewCreation';
import { logGlobalError } from 'shared/utils/globalErrorLogger';

/**
 * New-account creation/linking, ported from aligned's
 * UserService.createNewUserAccount + FirestoreApiService.createUserAccount.
 * Writes to the SHARED database — emulator-validated (newUserAccount-flow tests);
 * the exact document shape lives in `newUserAccountDoc.ts`.
 */

const USER_ACCOUNTS = 'user_accounts';
const LOOKUPS = 'user_account_lookups';
const USERS = 'users';

function usersDocUpdates(
  firebaseId: string,
  userAccountId: string,
  email: string,
  firstName: string | null,
  lastName: string | null,
): Record<string, unknown> {
  const updates: Record<string, unknown> = { id: firebaseId, userAccountId };
  if (email) updates.email = email;
  if (firstName) updates.firstName = firstName;
  if (lastName) updates.lastName = lastName;
  return updates;
}

function lookupPayload(
  userAccountId: string,
  firebaseId: string,
  normalized: string | null,
): Record<string, unknown> {
  // Dates are stored as ISO-8601 strings everywhere (project convention), not
  // Firestore Timestamps — even though aligned uses serverTimestamp() here for
  // this internal lookup metadata.
  const payload: Record<string, unknown> = { userAccountId, createdAt: new Date().toISOString() };
  if (firebaseId) payload.firebaseId = firebaseId;
  if (normalized) payload.email = normalized;
  return payload;
}

export interface CreateAccountResult {
  userAccountId: string;
  created: boolean;
}

/**
 * Create (or link) the current user's account. Mirrors aligned's two phases:
 *   1. If an account already exists for this email under a different uid, relink
 *      it (update `firebaseId` so the by-firebaseId lookup keeps working).
 *   2. Otherwise, in a transaction, create the account doc + `private/info`
 *      (with `unsub_<nanoid>`, plus `googleDriveEmail` for a Gmail signup) +
 *      email/firebase lookup docs + `users/{uid}`.
 */
export async function createNewUserAccount(user: FirebaseUser): Promise<CreateAccountResult> {
  const profile = profileFromUser(user);
  const email = profile.email;
  const firebaseId = user.uid;
  const normalized = normalizeEmail(email);

  // Phase 1: link an existing account found by email.
  if (email) {
    const existing = await getUserAccountByEmail(email);
    if (existing) {
      if (existing.firebaseId !== firebaseId) {
        await updateDoc(doc(db, USER_ACCOUNTS, existing.id), { firebaseId });
        await setDoc(doc(db, USERS, firebaseId), { userAccountId: existing.id }, { merge: true });
      }
      return { userAccountId: existing.id, created: false };
    }
  }

  // Phase 2: create (idempotent via lookups / users-doc pointer).
  const result = await runTransaction(async (tx) => {
    const emailKey = normalized ? `email:${normalized}` : null;
    const fbKey = `firebase:${firebaseId}`;

    // READS (must all precede writes in a Firestore JS transaction).
    const emailLookupSnap = emailKey ? await tx.get(doc(db, LOOKUPS, emailKey)) : null;
    const fbLookupSnap = await tx.get(doc(db, LOOKUPS, fbKey));
    const usersSnap = await tx.get(doc(db, USERS, firebaseId));

    let existingId: string | null = null;
    for (const snap of [emailLookupSnap, fbLookupSnap]) {
      if (existingId || !snap || !snap.exists()) continue;
      const mappedId = String(snap.data()?.userAccountId ?? '').trim();
      if (mappedId && (await tx.get(doc(db, USER_ACCOUNTS, mappedId))).exists()) {
        existingId = mappedId;
      }
    }
    if (!existingId && usersSnap.exists()) {
      const refId = String(usersSnap.data()?.userAccountId ?? '').trim();
      if (refId && (await tx.get(doc(db, USER_ACCOUNTS, refId))).exists()) {
        existingId = refId;
      }
    }

    // WRITES.
    if (existingId) {
      tx.set(
        doc(db, USERS, firebaseId),
        usersDocUpdates(firebaseId, existingId, email, profile.firstName, profile.lastName),
        { merge: true },
      );
      return { userAccountId: existingId, created: false };
    }

    const newRef = doc(collection(db, USER_ACCOUNTS));
    const id = newRef.id;
    tx.set(
      newRef,
      buildNewUserAccountDoc({
        id,
        firebaseId,
        email,
        firstName: profile.firstName,
        lastName: profile.lastName,
        profilePictureUrl: profile.profilePictureUrl,
        createdAt: new Date().toISOString(),
      }),
    );
    tx.set(
      doc(db, USER_ACCOUNTS, id, 'private', 'info'),
      buildNewPrivateInfoDoc({ userAccountId: id, firebaseId, email, unsubscribeToken: `unsub_${nanoid(30)}` }),
      { merge: true },
    );
    if (emailKey) tx.set(doc(db, LOOKUPS, emailKey), lookupPayload(id, firebaseId, normalized));
    tx.set(doc(db, LOOKUPS, fbKey), lookupPayload(id, firebaseId, normalized));
    tx.set(
      doc(db, USERS, firebaseId),
      usersDocUpdates(firebaseId, id, email, profile.firstName, profile.lastName),
      { merge: true },
    );
    return { userAccountId: id, created: true };
  });

  // Create the paired account_review for a freshly-created account, mirroring
  // aligned's createNewUserAccount. Best-effort: a failure here must not fail
  // signup — the review can be created later by the cloud-function fallback or
  // a re-run — matching aligned's "account review can be created later".
  if (result.created) {
    try {
      await createAccountReviewIfAbsent({
        expertId: result.userAccountId,
        firebaseId,
        expertEmail: email ?? '',
      });
    } catch (err) {
      logGlobalError(err, {
        context: 'createNewUserAccount.createAccountReview',
        metadata: { userAccountId: result.userAccountId },
      });
    }
  }

  return result;
}
